// ! Utility to help link sources

import config from '../../cfg';
import store from '../../redux/store';

import * as Selectors from '../../redux/selectors';
import * as NetworkActions from '../../redux/actions/network.actions';
import * as UIAction from '../../redux/actions/ui.actions';
import * as LinkHelper from '../../utils/LinkHelper';

import { disconnectLinkedSource, isEntityLinked, createOauthStateToken } from '../../api/client';

import { Buffer } from 'buffer';
import { poll } from '@hotglue/common';
import { Alert } from '../popupAlerts';
import { initializeOauth1Flow, updateLinkedSourceConfig } from '../../api/client';

export const handleDirectLinkSource = async (source, values = {}) => {
  const { type, tap, label, tenant_id, connection_name } = source;
  const updatedSource = Object.assign({}, source, values);
  const state = store.getState();
  const isConnected = state.sources.selectedSource && state.sources.selectedSource.tap === tap;

  if (type === 'oauth' && source['connect_ui_params']) {
    const params = source['connect_ui_params'];
    // Check if there are any flow specific params
    const flow_params = Object.entries(params).filter(([field, value]) => {
      let { flow_id } = value;

      return (
        flow_id &&
        flow_id === Selectors.getSelectedFlow() &&
        (!values.config || (values.config && values.config[field] === undefined))
      );
    });

    if (flow_params.length > 0) {
      // Open AppParams
      UIAction.showConnectParams(updatedSource);
      return;
    }
  }

  const response = await store.dispatch(NetworkActions.doAddLinkedSource(updatedSource));

  // Check if request was successful
  if (response?.status === 'error') {
    throw response.payload;
  }

  if (type === 'oauth' || (type === 'oauth1' && !updatedSource.symlink && !isConnected)) {
    const tap_url =
      type === 'oauth1'
        ? await initializeOauth1Flow(tap, values, undefined, tenant_id)
        : await getTapUrl(tap, values, undefined, tenant_id, connection_name);
    if (!tap_url) return;
    createAndPollLink(tap_url, label, tap, tenant_id);

    // Don't trigger listener until OAuth is linked
    return response;
  }

  // Alert listener if exists
  const listener = Selectors.getListener();
  if (listener && listener.onSourceLinked) {
    listener.onSourceLinked(source, Selectors.getSelectedFlow(), source.tenant_id ?? Selectors.getTenantId());
  }

  return response;
};

export const normalizeRedirectUri = (urlString, redirectUri, redirectUriKey = 'redirect_uri') => {
  const url = new URL(urlString);
  url.searchParams.set(redirectUriKey, encodeURI(redirectUri));
  return url.toString();
};

export const getTapUrl = async (
  sourceId,
  values = {},
  flow = Selectors.getSelectedFlow(),
  user_id = Selectors.getTenantId(),
  connection_name = undefined,
) => {
  const source = Selectors.selectSupportedSourceById(sourceId);
  if (!source) return undefined;
  let base_url = source.tap_url;

  if (values && values.config) {
    // Handle variables in base_url (used for Shopify)
    Object.entries(values.config).forEach(([k, v]) => {
      base_url = base_url.replace(`{${k}}`, v);
    });
  }

  if (sourceId === 'exact' && typeof source.client_id !== 'string') {
    // Get the region they selected
    let region = values?.config?.uri || values?.config?.auth_url?.split('/api')[0];

    // Set the base_url based on this region
    base_url = new URL(base_url);
    base_url.searchParams.set('client_id', source.client_id[region]);
    base_url = base_url.toString();
  }

  if (
    source.connect_ui_params?.client_id &&
    source.connect_ui_params?.client_secret &&
    values?.config?.client_id &&
    values?.config?.client_secret
  ) {
    base_url = new URL(base_url);
    base_url.searchParams.set('client_id', values.config.client_id);
    base_url = base_url.toString();
  }

  const env_id = Selectors.getEnvId();
  const api_key = Selectors.getApiKey();
  const callbackDomain = Selectors.getCallbackDomain();
  const callbackDomainName = Selectors.getCallbackDomainName();
  const callbackFavicon = Selectors.getCallbackFavicon();
  const redirectUri = Selectors.getRedirectUri();
  const hideText = Selectors.getHideText();
  const bgColor = Selectors.getBgColor();
  const skipCallbackOAuthErrors = Selectors.skipCallbackOAuthErrors();
  const api_url = config.api_url;
  const callbackUrl = redirectUri || config.redirect_url;

  // Build the state needed by the callback that completes OAuth flow
  const state = {
    user_id,
    env_id,
    flow,
    tap_name: source.tap,
    api_url: `${api_url}/tap/auth`,
    api_key,
    domain: callbackDomain || 'hotglue.xyz',
    domainName: callbackDomainName,
    favicon: callbackFavicon,
    connection_name,
    hideText,
    bgColor,
    skipCallbackOAuthErrors,
    popupShouldClose: true,
  };

  const stateTokenResponse = await createOauthStateToken(state);
  const stateToken = stateTokenResponse.token;

  let url = `${base_url}&state=HGTOKEN-${stateToken}`;

  if (!url.includes('redirect_uri=')) {
    url += `&redirect_uri=${callbackUrl}`;
  }

  if (source.tap === 'woocommerce:oauth') {
    url = url.replace('&state=', '&user_id=').replace('&redirect_uri=', '&return_url=');
    url += `&callback_url=${api_url}/tap/auth`;
  }

  if (source.tap === 'linnworks') {
    // Remove the client id, use the Tracking key instead of state
    url = url.replace('&client_id=', '/').replace('&state=', '?Tracking=');
    // drop the redirect uri
    url = new URL(url);
    url.searchParams.delete('redirect_uri');
    url = url.toString();
  }

  if (source.tap === 'snowflake:oauth') {
    // Snowflake requires callbackUrl to be url encoded
    url = normalizeRedirectUri(url, callbackUrl);
  }

  if (source.tap === 'faire:oauth') {
    url = new URL(url);
    url.searchParams.set('applicationId', url.searchParams.get('client_id'));
    url.searchParams.delete('client_id');
    url.searchParams.set('redirectUrl', url.searchParams.get('redirect_uri'));
    url.searchParams.delete('redirect_uri');
    url = url.toString();
  }

  return url;
};

// Polls to see if the source is linked
export const pollLink = (
  tapId,
  label,
  pollWindowClosed,
  tenantId = undefined,
  nextStep = store.getState().configuration.nextStep,
  isReconnect = undefined,
) => {
  /**
   * When the connector has "connect_ui_params", we first create a "config.json"
   * with the "connect_ui_params" data in it. But the flow is not linked yet.
   *
   * So we're adding a "is flow linked" check in order to determine if the
   * oauth linking is finished or not
   */
  const selectedFlowId = Selectors.getSelectedFlow();

  let retryCount = 0;

  // approximately 5 minutes of polling
  const maxRetryCount = 300;

  /**
   * We encountered a few cases where the "poll window" appears to close
   * right after it opens.
   *
   * To make the link work, we had disabled the "pollWindowClosed()" check.
   * That works but it created another "weird behavior" that if the user manually
   * close the "poll window", the poll continues until it reach the "maxRetryCount".
   *
   * To make it better, I'm (davi) creating this "shouldCheckWindowStatus" variable
   * that is going to control whether we make the "pollWindowClosed()" check or not.
   *
   * 1. If the "poll window" is closed before the poll starts, we should not check the
   *    "pollWindowClosed()" because we encountered the aforementioned error. Therefore,
   *    "shouldCheckWindowStatus" is going to be "false"
   * 2. If the "poll window" closes before the first 2 "request" function calls, we are
   *    going to consider that we encountered the same error. Therefore, "shouldCheckWindowStatus"
   *    is going to be "false"
   *
   * Also we're letting the poll do one more request in case the "poll window" closes. For that,
   * we're creating this "shouldCancelOnWindowClose" variable. When we first detect that the
   * "poll window" is closed, we just set this new variable to "true". So in the next iteration
   * we can actually stop the polling and check if the link was successful or not.
   * We had to do this because we've encountered a possible "race conditon" in which the link
   * was successfull but the widget showed that the link was canceled
   */
  let shouldCheckWindowStatus = !pollWindowClosed();
  let shouldCancelOnWindowClose = false;

  const { future, cancel } = poll({
    request: () => {
      // Don't call cancel() directly here, as it breaks the Promise chain
      // Instead, return a resolved Promise if needed
      if (!Selectors.getSelectedFlow()) {
        console.log('No selected flow, stopping poll');
        // Schedule the cancel for after this request completes
        setTimeout(() => cancel(), 0);
        // Return a resolved Promise with empty data to keep the chain intact
        return Promise.resolve([{ payload: [] }, { payload: [] }]);
      }

      if (shouldCheckWindowStatus) {
        if (pollWindowClosed() && retryCount < 2) {
          shouldCheckWindowStatus = false;
        } else if (pollWindowClosed()) {
          if (shouldCancelOnWindowClose) {
            // Schedule the cancel for after this request completes
            setTimeout(() => cancel(), 0);
          } else {
            shouldCancelOnWindowClose = true;
          }
        }
      }
      retryCount++;
      return Promise.all([
        store.dispatch(NetworkActions.fetchLinkedSource(tapId)),
        store.dispatch(NetworkActions.fetchLinkedFlows(tenantId)),
      ]);
    },
    pollingPeriod: 1000,
    shouldStop: (r) => {
      if (!Selectors.getSelectedFlow()) return true;

      const [{ payload: response }, { payload: linkedFlows }] = r;

      const isFlowLinked = Boolean(Array.isArray(linkedFlows) && linkedFlows.find(({ id }) => id === selectedFlowId));

      if (shouldCheckWindowStatus && retryCount >= 2 && pollWindowClosed()) {
        if (shouldCancelOnWindowClose) {
          return true;
        }
        shouldCancelOnWindowClose = true;
        return false;
      }

      if (Array.isArray(response) && response.length > 0) {
        const tap_config = tenantId ? response.find(({ tenant_id }) => tenant_id === tenantId) : response[0];

        if (isFlowLinked && tap_config && !tap_config.disconnected && !tap_config.isReconnecting) return true;

        return retryCount > maxRetryCount;
      }
      return retryCount > maxRetryCount;
    },
  });

  future
    .then((response) => {
      const [{ payload: unsortedLinkedSources }, { payload: linkedFlows }] = response;

      const isFlowLinked = Boolean(Array.isArray(linkedFlows) && linkedFlows.find(({ id }) => id === selectedFlowId));

      const linkedSources = unsortedLinkedSources.sort((a, b) => (a.tenant_id < b.tenant_id ? -1 : 1));

      const tenantLinkedSource = linkedSources.find(({ tenant_id }) => tenant_id === tenantId) ?? linkedSources[0];

      const listener = Selectors.getListener();

      const error = tenantLinkedSource?.error;

      if (error) {
        Alert({ type: 'error', text: `An error occurred while linking ${label}. ${error && error.message}` });
        if (error?.message && listener?.onSourceLinkFailed) {
          listener.onSourceLinkFailed(tapId, selectedFlowId, tenantId ?? Selectors.getTenantId(), error.message);
        }
        return LinkHelper.postLink(undefined, 'source', Selectors.getSelectedFlow()).finally(() => {
          UIAction.stopSourceLink();
        });
      }

      if (
        retryCount > maxRetryCount ||
        !isFlowLinked ||
        !linkedSources ||
        !(Array.isArray(linkedSources) && linkedSources.length > 0) ||
        tenantLinkedSource?.isReconnecting
      ) {
        Alert({ type: 'error', text: `Linking ${label} cancelled.` });
        // Alert listener if exists
        if (listener && listener.onSourceLinkCanceled) {
          listener.onSourceLinkCanceled(tapId, Selectors.getSelectedFlow());
        }

        const isEntityLinked =
          isFlowLinked && (linkedFlows.find(({ id }) => id === selectedFlowId)?.taps ?? []).includes(tapId);

        let promise = null;

        if (isEntityLinked && linkedSources && Array.isArray(linkedSources) && tenantLinkedSource?.isReconnecting) {
          promise = updateLinkedSourceConfig(tapId, { isReconnecting: false });
        }

        // If the poll was canceled and the "isEntityLinked" is false, we need to call "disconnectLinkedSource" to delete files in S3.
        // We need to do this because sometimes we would show that a tenant exists although this tenant was never linked
        if (!isEntityLinked) {
          promise = disconnectLinkedSource(tapId, selectedFlowId, tenantId);
        }

        const onFinally = () => UIAction.stopSourceLink();

        if (promise !== null) {
          promise.finally(() => {
            LinkHelper.postLink(undefined, 'source', Selectors.getSelectedFlow()).finally(onFinally);
          });
        } else {
          onFinally();
        }

        return;
      }

      LinkHelper.postLink(undefined, 'source', Selectors.getSelectedFlow()).then(() => {
        UIAction.stopSourceLink();
        Alert({ type: 'success', text: `${label} linked!` });
        if (nextStep === 'fieldMap') {
          UIAction.setOpenWidget();
          UIAction.goNextStep({ ...tenantLinkedSource, entityType: 'source' }, nextStep);
        } else if (
          nextStep === 'goToConfig' ||
          nextStep === 'mapping' ||
          nextStep === 'credentials' ||
          nextStep === 'triggers' ||
          nextStep === 'selectFile'
        ) {
          UIAction.setOpenWidget();
          UIAction.goToEntityConfig(
            tenantLinkedSource,
            'source',
            selectedFlowId,
            nextStep === 'credentials',
            nextStep === 'triggers',
            nextStep === 'selectFile' 
          );
        } else if (['google-sheets-beta', 'google-drive'].includes(tenantLinkedSource.tap))
          store.getState().ui.isConnection
            ? UIAction.openEntityConfig(tenantLinkedSource, 'source', selectedFlowId)
            : UIAction.goToEntityConfig(tenantLinkedSource, 'source', selectedFlowId, false, false, nextStep === 'selectFile');
        UIAction.setIsLinking(false);
        isReconnect && UIAction.selectEntity(tenantLinkedSource, 'source');

        const listener = Selectors.getListener();
        if (
          listener &&
          listener.onSourceLinked &&
          (linkedSources || (Array.isArray(linkedSources) && linkedSources.length > 0)) &&
          !tenantLinkedSource.disconnected &&
          !tenantLinkedSource.isReconnecting
        ) {
          const linkedSource = tenantLinkedSource;
          if (isReconnect !== undefined) {
            linkedSource.reconnected = isReconnect;
          }
          listener.onSourceLinked(linkedSource, Selectors.getSelectedFlow(), tenantId ?? Selectors.getTenantId());
        }
      });
    })
    .catch((e) => {
      console.error(e);
      Alert({ type: 'error', text: `An error occurred while linking ${label}. ${e}` });
      UIAction.stopSourceLink();
    });
};

export function createAndPollLink(
  tap_url,
  label,
  tap,
  tenantId = undefined,
  nextStep = undefined,
  isReconnect = undefined,
) {
  const windowClosed = { closed: false };
  const onClose = () => {
    windowClosed.closed = true;

    // Alert listener if exists
    const listener = Selectors.getListener();
    if (listener && listener.onPopupClose) {
      listener.onPopupClose(tap, Selectors.getSelectedFlow());
    }
  };
  const pollWindowClosed = () => windowClosed.closed;
  LinkHelper.createWindowPopup(tap_url, `Link your ${label} data`, 800, 600, onClose, 'tap');
  pollLink(tap, label, pollWindowClosed, tenantId, nextStep, isReconnect);
}

export const preLink = async ({
  hasLinkedSource,
  demo,
  source,
  flow,
  shouldSkipCopy = false,
  tenantId = undefined,
  setLoading,
}) => {
  if (hasLinkedSource) {
    return Alert({
      text: 'Disconnect linked source before switching to another',
      type: 'error',
    });
  }

  if (demo) {
    return Alert({
      type: 'info',
      text: `Sorry, you can't connect sources in demo mode!`,
    });
  }

  setLoading(true);

  const { tap } = source;

  // Check if source is already linked in another flow
  const oFlows = await isEntityLinked(tap);

  const filteredFlows = Array.isArray(oFlows) && oFlows.filter((f) => f.id !== flow);

  if (!shouldSkipCopy && filteredFlows?.length > 0) {
    setLoading(false);
    // Ask if they want to copy tap config from one, nextStep of these
    UIAction.showCopyEntity(source, oFlows);
  } else {
    await doLink(source, tenantId);

    setLoading(false);
  }
};

export const doLink = async (source, tenantId = undefined, nextStep, connectValues = undefined, checkLink = true) => {
  const { tap, type, label, connect_ui_params, connection_name, auto_link: autoLink } = source;

  if (tap === 'file') {
    try {
      // Link the file source
      await handleDirectLinkSource({ ...source, tenant_id: tenantId ?? source.tenant_id });
      // Refetch the flows
      await LinkHelper.postLink(tenantId);
      // Open file source
      UIAction.goToEntityConfig(source);
    } catch (err) {
      console.error(err);
    }
  } else if (connectValues || autoLink) {
    await handleDirectLinkSource(
      { ...source, tenant_id: tenantId ?? source.tenant_id },
      connectValues && { config: connectValues },
    );
  } else if (connect_ui_params) {
    UIAction.showConnectParams({ ...source, tenant_id: tenantId }, undefined, nextStep);
  } else if (type === 'oauth') {
    const tap_url = await getTapUrl(tap, source, undefined, tenantId, connection_name);
    if (!tap_url) return;
    if (!checkLink) {
      try {
        await updateLinkedSourceConfig(tap, { isReconnecting: true }, undefined, tenantId);
      } catch (err) {
        console.log(err)
      }
    }
    createAndPollLink(tap_url, label, tap, tenantId, nextStep);
  }
};

export const pollJob = async (jobRoot, flow, userId, entityId) => {
  const { jobs } = store.getState();
  const jobState = jobs.polling[`${flow}/${userId}`];

  let isPolling = undefined;
  if (jobState) isPolling = jobState[jobRoot];

  const jobTenantId = jobRoot.split('/')[0];
  if (jobTenantId !== userId) return {};

  if (isPolling !== undefined && isPolling) return {};
  else UIAction.setAPollingJob(jobRoot, flow, userId, true);

  const { future } = poll({
    request: () => store.dispatch(NetworkActions.fetchJobStatus(jobRoot, flow, userId, entityId)),
    pollingPeriod: 15000,
    shouldStop: ({ payload: job }) => {
      console.log(`Polling job ${jobRoot} - status=${job && job.status}`);
      return !job || job.status.includes('FAILED') || job.status.includes('COMPLETE');
    },
  });

  future.then(({ payload: job }) => {
    if (job.status.includes('FAILED')) Alert({ type: 'error', text: `Job completed with errors.` });
    else Alert({ type: 'success', text: 'Job completed successfully' });
    UIAction.setAPollingJob(jobRoot, flow, userId, false);
  });

  return future;
};
